import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";

import { useEffect, useRef, useState } from 'react';

const Mathit = ({ latex, display = false }) => {
  const spanRef = useRef(null);

  useEffect(() => {
    const loadKaTeX = () => {
      if (!window.katex) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.8/katex.min.css';
        document.head.appendChild(link);

        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.8/katex.min.js';
        script.onload = renderMath;
        document.head.appendChild(script);
      } else {
        renderMath();
      }
    };

    const renderMath = () => {
      if (spanRef.current && window.katex) {
        try {
          window.katex.render(latex, spanRef.current, {
            displayMode: display,
            throwOnError: false,
          });
        } catch (err) {
          console.error('KaTeX render error:', err);
          spanRef.current.textContent = latex;
        }
      }
    };

    loadKaTeX();
  }, [latex, display]);

  return (
    <span
      ref={spanRef}
      className={display ? 'block text-center my-4' : 'inline'}
    />
  );
};
const sections = [
  {
    title: "Supervised Learning",
    subsections: [
      { name: "Introduction", key: "supervised-introduction" },
      // { name: "Ridge", key: "supervised-ridge" },
      // { name: "Lasso", key: "supervised-lasso" }
    ],
    icon: <BookOpen className="w-4 h-4" />,
  },
  {
    title: "Unsupervised Learning",
    subsections: [
      // { name: "Clustering", key: "unsupervised-clustering" },
      // { name: "Dimensionality Reduction", key: "unsupervised-dim-reduction" },
      // { name: "Anomaly Detection", key: "unsupervised-anomaly-detection" }
    ],
  },
  {
    title: "Appendix",
    subsections: [
      { name: "Math-Integration", key: "appendix-math-integration" },
      { name: "Math-Statistics", key: "math-statistics" }
    ],
    icon: <BookOpen className="w-4 h-4" />,
  }
  // {
  //   title: "Linear Models",
  //   subsections: [
  //     // { name: "Linear Regression", key: "linear-linear-regression" },
  //     // { name: "Ridge", key: "linear-ridge" },
  //     // { name: "Lasso", key: "linear-lasso" }
  //   ],
  //   icon: <BookOpen className="w-4 h-4" />,
  // },
  // {
  //   title: "Tree-Based Models",
  //   subsections: [
  //     // { name: "Decision Trees", key: "tree-decision-trees" },
  //     // { name: "Random Forest", key: "tree-random-forest" },
  //     // { name: "Gradient Boosting", key: "tree-gradient-boosting" }
  //   ],
  //   icon: <Brain className="w-4 h-4" />,
  // },
  // {
  //   title: "Support Vector Machines",
  //   subsections: [
  //     // { name: "Linear SVM", key: "svm-linear-svm" },
  //     // { name: "Kernel SVM", key: "svm-kernel-svm" }
  //   ],
  //   icon: <Zap className="w-4 h-4" />,
  // },
];

const sectionContent = {
  "supervised-introduction": (
     <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="ml-content"
    >
      <div className="border-l-4 border-blue-600 pl-6">
        <h1>Supervised Learning</h1>
        <p>Foundational Machine Learning Paradigm</p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-8 rounded-lg border border-blue-100">
        <p>
          <i>Supervised learning</i> is the process of learning a function that maps inputs (X) to outputs (Y) using labeled training data.
        </p>
      </div>

      <div>
        <h3>Two simple methods to prediction</h3>
        <h4>1. Linear Models and Least Squares</h4>
        <p>
          In linear models, the form of regression is:
          <Mathit latex="Y = \beta_0 + \sum_{j=1}^{p} X_j \beta_j +\epsilon" display />
        </p>
        <p>
          Least squares method picks the <Mathit latex="\beta"/> minimizes the sum of squared differences between real and predicted values:
          <Mathit latex="\sum_{i=1}^{n} (Y_i - \hat{Y}_i)^2= \sum_{i=1}^{n} (Y_i - (\beta_0 + \sum_{j=1}^{p} X_{ij} \beta_j))^2" display />
        </p>
        <p>
          To solve this equation, we include a constant 1 in <Mathit latex="X"/> and <Mathit latex="\beta_0"/> into <Mathit latex="\beta"/>:
          <Mathit latex="\hat{Y} = \sum_{j=0}^{p} X_j \hat{\beta_j}" display />
        </p>
        <p>
          The solution is solved by differentiation and given by:
          <Mathit latex="\hat{\beta} = (X^TX)^{-1}X^TY" display />
        </p>
        <h4>2. Nearest-Neighbor Methods</h4>
        <p>
        Nearest-neighbor methods use observations in the training set to predict the output for a new observation. The prediction is made by averaging the outputs of the k-nearest neighbors in the training set.
        <Mathit latex="\hat{Y} = \frac{1}{k} \sum_{x_i \in N_k(x)} Y_i" display />
        </p>
        <h3>Metrics of performance</h3>
        <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-8 rounded-lg border border-blue-100">
      <p>
        Performance metrics help us evaluate how well our machine learning model is performing. 
        The <i>confusion matrix</i> provides a foundation for calculating key metrics.
      </p>
    </div>

    <div>
      <h3>Confusion Matrix</h3>
      <p>A confusion matrix summarizes the performance of a classification model by showing actual vs predicted classifications:</p>
      
      {/* Confusion Matrix Table */}
      <div className="my-8 flex justify-center">
        <div className="bg-white rounded-lg shadow-lg border-2 border-blue-200 overflow-hidden">
          <table className="border-collapse">
            <thead>
              <tr>
                <th className="bg-gray-100 border border-gray-300 p-4 text-center"></th>
                <th className="bg-blue-100 border border-gray-300 p-4 text-center font-bold text-blue-800">
                  Predicted Positive
                </th>
                <th className="bg-blue-100 border border-gray-300 p-4 text-center font-bold text-blue-800">
                  Predicted Negative
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th className="bg-yellow-100 border border-gray-300 p-4 text-center font-bold text-yellow-800">
                  Actual Positive
                </th>
                <td className="bg-blue-500 text-white border border-gray-300 p-6 text-center">
                  <div className="text-lg font-bold">TP</div>
                  <div className="text-sm">True Positive</div>
                </td>
                <td className="bg-yellow-200 text-yellow-800 border border-gray-300 p-6 text-center">
                  <div className="text-lg font-bold">FN</div>
                  <div className="text-sm">False Negative</div>
                </td>
              </tr>
              <tr>
                <th className="bg-yellow-100 border border-gray-300 p-4 text-center font-bold text-yellow-800">
                  Actual Negative
                </th>
                <td className="bg-blue-200 text-blue-800 border border-gray-300 p-6 text-center">
                  <div className="text-lg font-bold">FP</div>
                  <div className="text-sm">False Positive</div>
                </td>
                <td className="bg-yellow-400 text-gray-800 border border-gray-300 p-6 text-center">
                  <div className="text-lg font-bold">TN</div>
                  <div className="text-sm">True Negative</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
          <h4 className="text-blue-800 font-bold mb-3">Correct Predictions</h4>
          <p><strong>True Positive (TP):</strong> Correctly identified positive cases</p>
          <p><strong>True Negative (TN):</strong> Correctly identified negative cases</p>
        </div>
        <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-lg border border-red-200">
          <h4 className="text-red-800 font-bold mb-3">Prediction Errors</h4>
          <p><strong>False Positive (FP):</strong> Incorrectly predicted as positive (Type I Error)</p>
          <p><strong>False Negative (FN):</strong> Incorrectly predicted as negative (Type II Error)</p>
        </div>
      </div>

      <h3>Key Performance Metrics</h3>
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-6 rounded-lg border border-blue-100">
          <h4>Accuracy</h4>
          <Mathit latex="\text{Accuracy} = \frac{TP + TN}{TP + TN + FP + FN}" display />
          <p>Overall proportion of correct predictions</p>
        </div>
        
        <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-6 rounded-lg border border-blue-100">
          <h4>Precision</h4>
          <Mathit latex="\text{Precision} = \frac{TP}{TP + FP}" display />
          <p>Proportion of positive predictions that were actually correct</p>
        </div>
        
        <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-6 rounded-lg border border-blue-100">
          <h4>Recall (Sensitivity)</h4>
          <Mathit latex="\text{Recall} = \frac{TP}{TP + FN}" display />
          <p>Proportion of actual positives that were correctly identified</p>
        </div>
        
        <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-6 rounded-lg border border-blue-100">
          <h4>Specificity</h4>
          <Mathit latex="\text{Specificity} = \frac{TN}{TN + FP}" display />
          <p>Proportion of actual negatives that were correctly identified</p>
        </div>
      </div>
      </div>
      </div>
    </motion.div>
  ),
  "appendix-math-integration": (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="ml-content"
    >
      <div className="border-l-4 border-blue-600 pl-6">
        <h1>Appendix: Math Integration</h1>
        <p>Integrating Mathematical Concepts in Machine Learning</p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-8 rounded-lg border border-blue-100">
        <p>
          This section covers essential mathematical concepts used in machine learning, including calculus, linear algebra, and probability.
        </p>
      </div>

      <div>
        <h3>Integration Definition</h3>
        <p>
          Given a probability space <Mathit latex="(\Omega, \mathcal{F}, \mathbb{P})" />, for a measurable function <Mathit latex="X: \Omega \to \mathbb{R}"/>, the integral of <Mathit latex="X"/> with respect to the probability measure <Mathit latex="\mathbb{P}"/> is defined as:
          <Mathit latex="\int_{\Omega} X \, d\mathbb{P} = \mathbb{E}[X]" display />
        </p>
        <h4>
          Monotone Convergence Theorem
        </h4>
        <p>
          <Mathit latex="{X_n}_{n=1}^{\infty} "/>is a sequence of non-negative measurable and monotonely increasing functions:
          <Mathit latex="X_1(\omega) \leq X_2(\omega)\leq \cdots \uparrow X(\omega)" display />
          Then:
          <Mathit latex="\int_{\Omega} X_n \, d\mathbb{P} \to \int_{\Omega} X \, d\mathbb{P} \quad \text{or} \quad \mathbb{E}[X_n]\to \mathbb{E}[X]" display />
        </p>
        <h4>
          Dominated Convergence Theorem
        </h4>
        <p>
          If <Mathit latex="X_n\to X"/> almost surely, and there exists an integrable function <Mathit latex="Y"/> such that <Mathit latex="|X_n(\omega)| \leq Y(\omega)"/>, then:
          <Mathit latex="\int_{\Omega} X_n \, d\mathbb{P} \to \int_{\Omega} X \, d\mathbb{P} \quad \text{or} \quad \mathbb{E}[X_n]\to \mathbb{E}[X]" display />
        </p>
        <h4>
          Fatou's Lemma
        </h4>
        <p>
          If <Mathit latex="\{X_{n=1}^{\infty}\} "/> is a sequence of non-negative measurable functions, then:
          <Mathit latex="\int_{\Omega} \liminf_{n \to \infty} X_n \, d\mathbb{P} \leq \liminf_{n \to \infty} \int_{\Omega} X_n \, d\mathbb{P} \quad \text{or} \quad \mathbb{E}[\liminf_{n \to \infty} X_n] \leq \liminf_{n \to \infty} \mathbb{E}[X_n]" display />
        </p>
        <h4>
          Fubini's Theorem
        </h4>
        <p>
          If <Mathit latex="f(x,y)"/> is integrable over the product space <Mathit latex="X \times Y"/> and <Mathit latex="\mu"/> and <Mathit latex="\nu"/> are <Mathit latex="\sigma"/>-finite measures, then:
        <Mathit latex="\int_{X} \int_{Y} f(x,y) \, d\nu(y) \, d\mu(x) = \int_{Y} \int_{X} f(x,y) \, d\mu(x) \, d\nu(y)= \int_{X \times Y} f(x,y) \, d(\mu \times \nu)(x,y)" display />
        or
        <Mathit latex="\mathbb{E}[\mathbb{E}[f(X,Y)|Y]] = \mathbb{E}[\mathbb{E}[f(X,Y)|X]] = \mathbb{E}[f(X,Y)]" display />
        </p>
        <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-8 rounded-lg border border-blue-100 mt-6">
          <h5>Bayesian Neural Networks</h5>
          <p>
            In Bayesian neural networks, given input <Mathit latex="x^*"/>, the output <Mathit latex="y^*"/>'s distribution is defined as:
            <Mathit latex="p(y^*|x^*,D) = \int p(y^*|x^*,\omega) p(\omega|D) d\omega" display />
            </p>
          <p>
            We always need to compute <Mathit latex="\mathbb{E}_{\omega\sim p(\omega |D)}[\mathbb{E}_{y^* \sim p(y^*|x^*,\omega)}f(y)]"/>. Fubini's theorem allows us to use samples from all outputs under all <Mathit latex="\omega"/> to compute the expectation <Mathit latex="E_{\omega, y^*}[f(y)]"/>.
          </p>
        </div>
      </div>
    </motion.div>
  ),
  "math-statistics": (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="ml-content"
    >
      <div className="border-l-4 border-blue-600 pl-6">
        <h1>Math Statistics</h1>
        <p>Mathematical Foundations of Statistics</p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-yellow-50 p-8 rounded-lg border border-blue-100">
        <p>
          This section covers essential mathematical concepts used in statistics, including probability distributions, expectations, and variance.
        </p>
      </div>
      <h3>t distribution</h3>
      <p>
       假设有一个正态分布的随机变量 <Mathit latex="X \sim N(\mu, \sigma^2)"/>，我们可以通过样本均值和样本标准差来估计总体均值和标准差。t 分布用于小样本情况下的均值估计。
      </p>
      <p>
        构造 z 分布的随机变量 <Mathit latex="Z = \dfrac{\bar{X} - \mu}{\sigma / \sqrt{n}}"/>，其中 <Mathit latex="\bar{X}"/> 是样本均值，<Mathit latex="\sigma"/> 是总体标准差，<Mathit latex="n"/> 是样本大小。这个分布服从标准正态分布 <Mathit latex="N(0, 1)"/>。
      </p>
      <p>
        构造 t 分布的随机变量 <Mathit latex="T = \dfrac{\bar{X} - \mu}{S / \sqrt{n}}"/>，其中 <Mathit latex="\bar{X}"/> 是样本均值，<Mathit latex="S"/> 是样本标准差，<Mathit latex="n"/> 是样本大小。t 分布的自由度为 <Mathit latex="n-1"/>。
      </p>
      <p>
        t 分布的概率密度函数为：
        <Mathit latex="f(t) = \dfrac{\Gamma(\frac{n-1}{2})}{\sqrt{(n-1)\pi} \Gamma(\frac{n}{2})} \left(1 + \dfrac{t^2}{n-1}\right)^{-\frac{n}{2}}" display />
      </p>
      <h4> T 分布的性质</h4>
      <p>
        t 分布的均值为 0，方差为 <Mathit latex="\dfrac{n-1}{n-3}"/>（当 <Mathit latex="n > 3"/> 时）。随着样本大小 <Mathit latex="n"/> 的增加，t 分布逐渐接近标准正态分布。
      </p>
      <p>
        t 分布的形状与样本大小有关。样本量越小，t 分布的尾部越厚，表示更大的不确定性。随着样本量的增加，t 分布逐渐接近标准正态分布。这个好处是在做假设检验的时候会更加严谨，使推断更加保守。
      </p>
      {/* <h3></h3> */}
      {/* Content for Math Statistics */}
    </motion.div>
  ),
};

export default function MLModels() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expanded, setExpanded] = useState([sections[0].title]);
  const [selected, setSelected] = useState(sections[0].subsections[0].key);

  const toggleSection = (title) => {
    setExpanded((prev) =>
      prev.includes(title)
        ? prev.filter((t) => t !== title)
        : [...prev, title]
    );
  };

  return (
    <div className="flex h-screen font-serif bg-gray-50 text-gray-900">
  {/* 左侧 Sidebar 固定高度，不滚动 */}
  <AnimatePresence mode="wait">
    {sidebarOpen ? (
      <motion.div
        key="sidebar-open"
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: 320, opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="bg-white border-r border-gray-200 flex flex-col shadow-lg overflow-hidden h-full"
      >
        {/* 头部区域 */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <h1 className="text-xl font-light tracking-wide">Machine Learning</h1>
          <p className="text-blue-100 text-sm font-light mt-1">Statistical Learning Theory</p>
        </div>

        {/* 菜单区域（可滚动） */}
        <div className="flex-1 overflow-auto p-6">
          <nav className="space-y-4">
            {sections.map((section) => (
              <div key={section.title} className="border-b border-gray-100 pb-4 last:border-b-0">
                <button
                  onClick={() => toggleSection(section.title)}
                  className="w-full flex items-center justify-between text-left p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-blue-600">{section.icon}</div>
                    <span className="font-medium text-gray-800 text-sm tracking-wide">
                      {section.title}
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: expanded.includes(section.title) ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {expanded.includes(section.title) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pl-7 pt-3 space-y-2">
                        {section.subsections.map((sub) => (
                          <button
                            key={sub.key}
                            onClick={() => setSelected(sub.key)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                              selected === sub.key
                                ? "bg-gradient-to-r from-blue-50 to-yellow-50 text-blue-700 font-medium border-l-2 border-blue-600"
                                : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                            }`}
                          >
                            {sub.name}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>
        </div>

        {/* Collapse 按钮（固定底部） */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={() => setSidebarOpen(false)}
            className="w-full flex items-center justify-center p-3 rounded-lg hover:bg-gray-50 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            title="Collapse Navigation"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            <span className="text-sm font-light">Collapse</span>
          </button>
        </div>
      </motion.div>
    ) : (
      <motion.div
        key="sidebar-closed"
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: 64, opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="bg-white border-r border-gray-200 flex flex-col items-center justify-center shadow-lg overflow-hidden h-full"
      >
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-4 rounded-lg hover:bg-gray-50 text-gray-600 hover:text-blue-600 transition-colors duration-200"
          title="Expand Navigation"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </motion.div>
    )}
  </AnimatePresence>

  {/* 右侧内容区域可滚动 */}
  <div className="flex-1 overflow-y-auto">
    <div className="max-w-4xl mx-auto p-8">
      <AnimatePresence mode="wait">
        <motion.div key={selected}>
          {sectionContent[selected] || (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg font-light">Content not available</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  </div>
</div>
  );
}